import React, { useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { getGuideAccent } from "../../utils/guideAccent";

// Template324 (Edimburgo, copia de 124): Excursiones fuera de Londres (Tours.jsx guia-londres). Todo desde backend.
// page.texts[0]=header, texts[1]=intro | sections[0..3]=Harry Potter, Oxford, Stonehenge, Bath
const Template324 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [failedStickerUrls, setFailedStickerUrls] = useState(() => new Set());

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;
	const introText = page.texts?.[1]?.content;

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const actualHeaderColor = headerBackgroundColor || "#de5624";
	const accent = getGuideAccent(actualHeaderColor);
	const contentColor = textColor || "#444";

	const box = {
		backgroundColor: accent.boxBg,
		border: `2px solid ${accent.boxBorder}`,
		borderRadius: "16px",
		padding: "16px",
	};

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		marginBottom: "8px",
		color: accent.accent,
		marginTop: 0,
	};

	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	const linkStyle = {
		color: accent.accent,
		fontWeight: "bold",
		textDecoration: "none",
	};

	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const s0 = sections[0];
	const s1 = sections[1];
	const s2 = sections[2];
	const s3 = sections[3];

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{/* CABECERA */}
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
					}}
				>
					{headerText}
					{logoImage && !isSmallScreen && (
						<Box
							component="img"
							src={logoImage}
							alt="logo"
							sx={{
								width: isSmallScreen ? 66 : 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			{/* INTRO */}
			{introText && (
				<Box
					data-block-id="text-1"
					sx={{ padding: "12px 16px", fontSize: 13, color: contentColor }}
				>
					<Box component="p" sx={p}>
						{introText}
					</Box>
				</Box>
			)}

			{/* BLOQUES */}
			<Box
				sx={{
					padding: "0 16px 20px",
					display: "flex",
					flexDirection: "column",
					gap: "10px",
					position: "relative",
					paddingBottom: isSmallScreen ? "20px" : "0px",
				}}
			>
				{/* Harry Potter Studios */}
				{s0 && (
					<Box data-block-id="text-2" sx={{ position: "relative" }}>
						<Box sx={box}>
							{s0.title && (
								<Box component="h3" sx={h3}>
									{s0.title}
								</Box>
							)}
							{s0.body && (
								<Box component="p" sx={p}>
									{s0.body}
								</Box>
							)}
							{s0.location && (
								<Box component="p" sx={p}>
									{s0.location}
								</Box>
							)}
							{s0.reserveLink?.url && (
								<Box component="p" sx={p}>
									<a
										href={s0.reserveLink.url}
										target="_blank"
										rel="noopener noreferrer"
										style={linkStyle}
									>
										{s0.reserveLink.text}
									</a>
								</Box>
							)}
						</Box>
					</Box>
				)}

				{(s0?.stickerLeftCanvasId || s0?.stickerRightCanvasId) && (
					<Box
						sx={{
							position: "relative",
							height: isSmallScreen ? 78 : 86,
							overflow: "visible",
							flexShrink: 0,
						}}
					>
						{s0.stickerLeftCanvasId &&
							getCanvasImageUrl(s0.stickerLeftCanvasId) &&
							!failedStickerUrls.has(
								getCanvasImageUrl(s0.stickerLeftCanvasId),
							) && (
								<img
									src={getCanvasImageUrl(s0.stickerLeftCanvasId)}
									alt=""
									onError={(e) =>
										e?.target?.src &&
										setFailedStickerUrls((prev) =>
											new Set(prev).add(e.target.src),
										)
									}
									style={{
										width: 95,
										position: "absolute",
										left: -22,
										bottom: isSmallScreen ? -120 : -6,
										zIndex: 5,
									}}
								/>
							)}
						{s0.bubbleLeft && (
							<Box
								sx={{
									position: "absolute",
									left: 68,
									top: isSmallScreen ? 100 : -5,
									backgroundColor: "#fff8db",
									border: "2px solid #f5c400",
									borderRadius: "16px 16px 16px 0px",
									padding: "8px 10px",
									fontSize: 11,
									fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
									color: contentColor,
									boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
									width: isSmallScreen ? 240 : 175,
									zIndex: 6,
									lineHeight: 1.35,
								}}
							>
								{s0.bubbleLeft}
							</Box>
						)}
						{s0.stickerRightCanvasId &&
							getCanvasImageUrl(s0.stickerRightCanvasId) &&
							!failedStickerUrls.has(
								getCanvasImageUrl(s0.stickerRightCanvasId),
							) && (
								<img
									src={getCanvasImageUrl(s0.stickerRightCanvasId)}
									alt=""
									onError={(e) =>
										e?.target?.src &&
										setFailedStickerUrls((prev) =>
											new Set(prev).add(e.target.src),
										)
									}
									style={{
										width: 95,
										position: "absolute",
										right: -18,
										bottom: isSmallScreen ? -20 : -6,
										zIndex: 5,
									}}
								/>
							)}
						{s0.bubbleRight && (
							<Box
								sx={{
									position: "absolute",
									right: 68,
									top: -5,
									backgroundColor: "#fff8db",
									border: "2px solid #f5c400",
									borderRadius: "16px 16px 0px 16px",
									padding: "8px 10px",
									fontSize: 11,
									fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
									color: contentColor,
									boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
									width: isSmallScreen ? 230 : 175,
									zIndex: 6,
									lineHeight: 1.35,
								}}
							>
								{s0.bubbleRight}
							</Box>
						)}
					</Box>
				)}

				{/* Oxford */}
				{s1 && (
					<Box
						data-block-id="text-3"
						sx={{
							...box,
							marginTop: isSmallScreen ? "100px" : 0,
						}}
					>
						{s1.title && (
							<Box component="h3" sx={h3}>
								{s1.title}
							</Box>
						)}
						{s1.body && (
							<Box component="p" sx={p}>
								{s1.body}
							</Box>
						)}
						{s1.location && (
							<Box component="p" sx={p}>
								{s1.location}
							</Box>
						)}
						{s1.reserveLink?.url && (
							<Box component="p" sx={p}>
								<a
									href={s1.reserveLink.url}
									target="_blank"
									rel="noopener noreferrer"
									style={linkStyle}
								>
									{s1.reserveLink.text}
								</a>
							</Box>
						)}
					</Box>
				)}

				{/* Stonehenge */}
				{s2 && (
					<Box data-block-id="text-4" sx={{ position: "relative" }}>
						<Box sx={box}>
							{s2.title && (
								<Box component="h3" sx={h3}>
									{s2.title}
								</Box>
							)}
							{s2.body && (
								<Box component="p" sx={p}>
									{s2.body}
								</Box>
							)}
							{s2.location && (
								<Box component="p" sx={p}>
									{s2.location}
								</Box>
							)}
							{s2.reserveLink?.url && (
								<Box component="p" sx={p}>
									<a
										href={s2.reserveLink.url}
										target="_blank"
										rel="noopener noreferrer"
										style={linkStyle}
									>
										{s2.reserveLink.text}
									</a>
								</Box>
							)}
						</Box>
						{s2.stickerRightCanvasId &&
							getCanvasImageUrl(s2.stickerRightCanvasId) && (
								<img
									src={getCanvasImageUrl(s2.stickerRightCanvasId)}
									alt=""
									style={{
										width: isSmallScreen ? 100 : 110,
										position: "absolute",
										bottom: -65,
										right: -30,
									}}
								/>
							)}
					</Box>
				)}

				{/* Bath */}
				{s3 && (
					<Box
						data-block-id="text-5"
						sx={{
							...box,
							marginBottom: isSmallScreen ? 1 : 0,
						}}
					>
						{s3.title && (
							<Box component="h3" sx={h3}>
								{s3.title}
							</Box>
						)}
						{s3.body && (
							<Box component="p" sx={p}>
								{s3.body}
							</Box>
						)}
						{s3.reserveLink?.url && (
							<Box component="p" sx={p}>
								<a
									href={s3.reserveLink.url}
									target="_blank"
									rel="noopener noreferrer"
									style={linkStyle}
								>
									{s3.reserveLink.text}
								</a>
							</Box>
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
};

Template324.thumbnail = "/images/template-1.png";

export default Template324;
